import { ReclamationCustomerOrdersDataGridApi } from "src/api/generated/erp/reclamations/api/reclamationCustomerOrdersDataGridApi.ts";
import { CustomerOrderReclamationView } from "src/api/generated/erp/db/types/tables/customerOrderReclamationView.ts";
import { dateColumn, enumColumn, iconColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import { getCustomerOrderStateLabels } from "src/api/generated/erp/db/types/enums/customerOrderState.ts";
import { faBan, faCheck, faLinkSlash } from "@fortawesome/pro-regular-svg-icons";
import i18n from "i18next";
import { useTheme } from "@mui/material";
import { useTenantCustomizations } from "src/tenantCustomizations/TenantCustomizationsContext.ts";
import { associate } from "src/utils/arrayUtils.ts";
import { OpenCustomerOrderButton } from "src/components/views/erp/utilComponents/OpenCustomerOrderButton.tsx";
import { RefreshableElementProps } from "src/utils/useRefreshRef.ts";
import { useClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/useClientSideDataGridModel.tsx";
import { ControlledAsyncFormCrudDataGrid } from "src/components/common/dataGrid/crud/ControlledAsyncFormCrudDataGrid.tsx";
import { AsyncMenuButton } from "src/components/common/contextMenu/AsyncMenuButton.tsx";
import { ReclamationOperationApi } from "src/api/generated/erp/reclamations/api/reclamationOperationApi.ts";
import { useConfirmDialog } from "src/components/common/dialogs/confirmDialog/ConfirmDialogContext.ts";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface ReclamationCustomerOrdersDataGridProps extends RefreshableElementProps {
	reclamationId: number;
	onSelectionChanged: (selectedCustomerOrders: CustomerOrderReclamationView[]) => void;
}

export const ReclamationCustomerOrdersDataGrid = ({
	refreshRef,
	reclamationId,
	onSelectionChanged,
}: ReclamationCustomerOrdersDataGridProps) => {
	const theme = useTheme();
	const { tenantConfig } = useTenantCustomizations();
	const tenantStateLabelsByKeys = associate(
		tenantConfig.erp.customerOrderTenantStates,
		(state) => state.key,
		(state) => state.label,
	);

	const showConfirmDialog = useConfirmDialog();
	const hasPermission = useHasPermission();

	const { dataGridProps, refreshData } = useClientSideDataGridModel({
		fetchData: () =>
			ReclamationCustomerOrdersDataGridApi.getReclamationCustomerOrders({
				reclamationId: reclamationId,
			}),
		initialParams: {},
		getRowId: (row) => row.customerOrderId,
		customFunctionUiActionView: "CUSTOMER_ORDERS",
		onSelectionChanged: (rows) => {
			onSelectionChanged(rows);
		},
		gridId: "FD8C804AEE67A83A",
		refreshRef: refreshRef,
	});

	return (
		<ControlledAsyncFormCrudDataGrid<CustomerOrderReclamationView>
			columns={[
				iconColumn({
					field: "isPrimaryCustomerOrder",
					headerName: "",
					width: 50,
					getIconProps: ({ isPrimaryCustomerOrder }) =>
						isPrimaryCustomerOrder ?
							{
								icon: faCheck,
								color: theme.palette.success.main,
								tooltip: i18n.t("primary"),
							}
						:	{
								icon: faBan,
								color: theme.palette.grey.A400,
								tooltip: i18n.t("secondary"),
							},
				}),
				textColumn({
					headerName: i18n.t("order_number"),
					field: "customerOrderId",
				}),
				textColumn({
					headerName: i18n.t("reference"),
					field: "orderReference",
					width: 300,
				}),
				dateColumn({
					headerName: i18n.t("planned_delivery_date"),
					field: "plannedDeliveryDate",
					width: 180,
				}),
				enumColumn({
					headerName: i18n.t("state"),
					field: "customerOrderState",
					enumLabels: getCustomerOrderStateLabels(),
					width: 150,
				}),
				tenantConfig.erp.useCustomerOrderTenantStates &&
					textColumn({
						headerName: tenantConfig.erp.customerOrderTenantStateColumnLabel,
						field: "tenantState",
						valueGetter: (_, row) => tenantStateLabelsByKeys[row.tenantState ?? ""] ?? "-",
					}),
			]}
			rowContextMenuComponents={({ onlySingleRowSelected, row }) => [
				onlySingleRowSelected && hasPermission(Permissions.Sales.CustomerOrders.READ) && (
					<OpenCustomerOrderButton key={"openCustomerOrder"} customerOrderId={row.customerOrderId} />
				),
				hasPermission(Permissions.Reclamations.MANAGE) && onlySingleRowSelected && (
					<AsyncMenuButton
						key={"removeConnection"}
						label={i18n.t("remove_connection")}
						icon={faLinkSlash}
						onClick={async () => {
							const confirmed = await showConfirmDialog({});
							if (!confirmed) return;
							await ReclamationOperationApi.removeConnectionToCustomerOrder({
								reclamationId: reclamationId,
								customerOrderId: row.customerOrderId,
							});
							await refreshData();
						}}
					/>
				),
			]}
			agentContext={{
				key: defineAgentUiContextKey("erp.reclamation.customerOrders.grid"),
				data: { reclamationId },
				rowFields: ["customerOrderId", "orderReference", "customerOrderState"],
				selectedRowPathSegment: (row) => `${i18n.t("order")} ${row.customerOrderId}`,
				selectedRowsPathSegment: (count) => i18n.t("reclamation_customer_orders_selected_count", { count }),
			}}
			{...dataGridProps}
		/>
	);
};
